import React, { useEffect } from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import Logo from '../components/logo/Logo';

export default function PrivacyPolicy() {
    useEffect(() => {
        document.title = "Privacy Policy - Zilean";
    }, []);

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
            <div className="max-w-4xl mx-auto px-4 py-12">
                {/* Logo and Back Link */}
                <div className="flex items-center justify-between mb-12">
                    <Link to="/" className="hover:opacity-90 transition-opacity">
                        <Logo />
                    </Link>
                    <Link 
                        to="/" 
                        className="text-sm text-primary hover:text-primary/90 transition-colors"
                    >
                        ← Back to Zilean
                    </Link>
                </div>

                {/* Header Section */}
                <div className="mb-12">
                    <h1 className="text-3xl font-bold tracking-tight">Privacy Policy</h1>
                    <p className="text-muted-foreground mt-2">
                        Last updated: March 16, 2024
                    </p>
                </div>

                <motion.div 
                    className="space-y-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <section className="space-y-4">
                        <h2 className="text-2xl font-semibold tracking-tight">Introduction</h2>
                        <p className="text-muted-foreground leading-relaxed">
                            At Zilean, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our time tracking and productivity application.
                        </p>
                    </section>

                    <section className="space-y-4">
                        <h2 className="text-2xl font-semibold tracking-tight">Information We Collect</h2>
                        <div className="space-y-4">
                            <h3 className="text-xl font-medium">Personal Information</h3>
                            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                                <li>Name and email address when you create an account</li>
                                <li>Profile information you provide</li>
                                <li>Authentication data when you sign in with Google</li>
                            </ul>

                            <h3 className="text-xl font-medium">Usage Data</h3>
                            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                                <li>Activity tracking data you input</li>
                                <li>Time spent on activities</li>
                                <li>Goals and habits you set</li>
                                <li>Analytics data about how you use the application</li>
                            </ul>
                        </div>
                    </section>

                    <section className="space-y-4">
                        <h2 className="text-2xl font-semibold tracking-tight">How We Use Your Information</h2>
                        <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                            <li>To provide and maintain our service</li>
                            <li>To notify you about changes to our service</li>
                            <li>To provide customer support</li>
                            <li>To gather analysis or valuable information to improve our service</li>
                            <li>To monitor the usage of our service</li>
                            <li>To detect, prevent and address technical issues</li>
                        </ul>
                    </section>

                    <section className="space-y-4">
                        <h2 className="text-2xl font-semibold tracking-tight">Data Security</h2>
                        <p className="text-muted-foreground leading-relaxed">
                            We use industry-standard security measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
                        </p>
                    </section>

                    <section className="space-y-4">
                        <h2 className="text-2xl font-semibold tracking-tight">Google Analytics</h2>
                        <p className="text-muted-foreground leading-relaxed">
                            We use Google Analytics to help us understand how our users use the site. Google Analytics collects standard internet log information and visitor behavior information in a way that doesn't identify anyone.
                        </p>
                    </section>

                    <section className="space-y-4">
                        <h2 className="text-2xl font-semibold tracking-tight">Your Rights</h2>
                        <p className="text-muted-foreground leading-relaxed">
                            You have the right to:
                        </p>
                        <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                            <li>Access your personal data</li>
                            <li>Correct any inaccurate personal data</li>
                            <li>Request deletion of your personal data</li>
                            <li>Object to processing of your personal data</li>
                            <li>Request restriction of processing your personal data</li>
                            <li>Request transfer of your personal data</li>
                        </ul>
                    </section>

                    <section className="space-y-4">
                        <h2 className="text-2xl font-semibold tracking-tight">Changes to This Privacy Policy</h2>
                        <p className="text-muted-foreground leading-relaxed">
                            We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date at the top of this Privacy Policy.
                        </p>
                    </section>
                </motion.div>

                {/* Footer */}
                <div className="mt-16 pt-8 border-t border-border text-center text-sm text-muted-foreground">
                    <p>© {new Date().getFullYear()} Zilean. All rights reserved.</p>
                </div>
            </div>
        </div>
    );
} 